/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-04T18:20:00+05:00
Intent: The web-link validator moved to utils/web-url.util so other forms share it; it now rejects hosts like ".com" and "example..com".
*/
import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { webUrlValidator } from 'src/app/utils/web-url.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';

import { BrandingService, CreateBrandPayload, UpdateBrandPayload } from '@services/branding.service';
import { SharedService } from '@services/shared.service';

export interface BrandItem {
  id: number | string; // uuid on the gateway
  name: string;
  description?: string | null;
  display_name?: string | null;
  primary?: string;
  secondary?: string;
  is_default?: boolean;
  is_active?: boolean;
  brand?: any;
  logo?: any;
  logo_dark?: any;
  icon?: any;
  og_image?: any;
  domains?: any[];
  live_domain_count?: number;
  version?: number;
  created_at?: number;
  updated_at?: number;
  [key: string]: any;
}

export interface DomainItem {
  [x: string]: any;
  id: number | string; // uuid on the gateway
  branding_id: number | string;
  hostname: string;
  kind: 'subdomain' | 'custom';
  status: 'live' | 'pending' | 'failed';
  is_primary?: boolean;
  canonical_host?: string;
  notes?: string;
  dns_setup?: any;
  created_at?: number;
  // domain/list returns the mode and a short hash of the key. It never returns
  // zero_storage_key, and nothing here may ask for it again.
  zero_storage?: boolean;
  zero_storage_key_hint?: string | null;
}

type AssetField = 'logo_url' | 'logo_dark_url' | 'icon_url';

/** Allowed [min, max] for the typography & shape number fields, also quoted in their error text. */
export const BRAND_NUMBER_LIMITS = {
  base_size: [12, 24],
  radius: [0, 40],
  button_radius: [0, 40],
  shell_max: [320, 1200],
} as const;

/** The sites each social link has to point at (subdomains such as www. or m. are fine). */
export const SOCIAL_LINK_HOSTS: Record<string, string[]> = {
  social_x: ['x.com', 'twitter.com'],
  social_instagram: ['instagram.com', 'instagr.am'],
  social_youtube: ['youtube.com', 'youtu.be'],
  social_tiktok: ['tiktok.com'],
  social_snapchat: ['snapchat.com'],
  social_linkedin: ['linkedin.com'],
};

// Lives in utils now so other forms (the Link level) share the rule; re-exported for existing imports.
export { webUrlValidator };

/** A whole number inside BRAND_NUMBER_LIMITS[field]. */
function brandNumberValidators(field: keyof typeof BRAND_NUMBER_LIMITS): ValidatorFn[] {
  const [min, max] = BRAND_NUMBER_LIMITS[field];
  return [Validators.required, Validators.min(min), Validators.max(max), Validators.pattern(/^\d+$/)];
}

@Component({
  selector: 'app-branding',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzModalModule,
    NzTableModule,
    NzToolTipModule,
    NzTabsModule,
    NzTagModule,
    NzSelectModule,
    NzSwitchModule,
  ],
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss'],
})
export class BrandingComponent implements OnInit {
  private fb = inject(FormBuilder);
  private brandingService = inject(BrandingService);
  private translate = inject(TranslateService);
  private cdRef = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private modal = inject(NzModalService);
  sharedService = inject(SharedService);

  @ViewChild('logoFileInput') logoFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('logoDarkFileInput') logoDarkFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('iconFileInput') iconFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('jsonFileInput') jsonFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('editJsonFileInput') editJsonFileInput!: ElementRef<HTMLInputElement>;

  isLoading = false;
  brands: BrandItem[] = [];
  domains: DomainItem[] = [];
  activeTab: 'brands' | 'domains' = 'brands';

  // Brand Modal state
  isBrandModalVisible = false;
  isSavingBrand = false;
  editingBrand: BrandItem | null = null;
  brandForm!: FormGroup;

  // Active theme tab in color picker modal
  colorThemeTab: 'light' | 'dark' = 'light';

  // Domain Modal state
  isDomainModalVisible = false;
  isSavingDomain = false;
  editingDomain: DomainItem | null = null;
  domainForm!: FormGroup;

  // JSON Edit Modal state
  isJsonModalVisible = false;
  isSavingJson = false;
  isExampleJsonModal = false;
  jsonModalBrand: BrandItem | null = null;
  rawJsonString = '';
  jsonError = '';
  isCopied = false;

  // JSON Upload Modal state
  isUploadJsonModalVisible = false;
  isSavingUploadJson = false;
  uploadJsonBrand: BrandItem | null = null;
  uploadedJsonFileName = '';
  uploadedJsonData: any = null;
  uploadJsonError = '';
  isDragging = false;

  // Asset Upload Dropzone Handlers
  selectedFiles: { [key in 'logo_url' | 'logo_dark_url' | 'icon_url']?: File | null } = {};
  uploadedAssetObjects: { [key in 'logo_url' | 'logo_dark_url' | 'icon_url']?: any } = {};
  originalBrandAssets: { [key in 'logo_url' | 'logo_dark_url' | 'icon_url']?: any } = {};

  triggerFileInput(field: 'logo_url' | 'logo_dark_url' | 'icon_url'): void {
    if (field === 'logo_url' && this.logoFileInput) {
      this.logoFileInput.nativeElement.click();
    } else if (field === 'logo_dark_url' && this.logoDarkFileInput) {
      this.logoDarkFileInput.nativeElement.click();
    } else if (field === 'icon_url' && this.iconFileInput) {
      this.iconFileInput.nativeElement.click();
    }
  }

  // Inline upload problems per logo field (a notification would close the brand modal).
  assetErrors: { [key in AssetField]?: string } = {};

  /** By type and extension; the picked file must also decode as an image before it is used. */
  isImageFile(file: File): boolean {
    return /^image\//i.test(file.type || '') && /\.(png|jpe?g|gif|webp|svg|ico|bmp|avif)$/i.test(file.name || '');
  }

  private rejectAssetFile(field: AssetField, input: HTMLInputElement): void {
    input.value = '';
    this.assetErrors[field] = 'branding.asset-not-image';
    this.cdRef.detectChanges();
  }

  onFileSelected(event: Event, field: 'logo_url' | 'logo_dark_url' | 'icon_url'): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      if (!this.isImageFile(file)) {
        this.rejectAssetFile(field, input);
        return;
      }
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        const result = e.target.result as string;
        // A renamed non-image still carries an image extension; only a real image decodes.
        const decodes = await new Promise<boolean>((resolve) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = result;
        });
        if (!decodes) {
          this.rejectAssetFile(field, input);
          return;
        }
        delete this.assetErrors[field];
        this.selectedFiles[field] = file;
        this.brandForm.patchValue({ [field]: result });
        this.brandForm.get(field)?.markAsDirty();
        this.brandForm.get(field)?.markAsTouched();
        this.cdRef.detectChanges();

        // Call baseToImage API with payload { url: result }
        this.sharedService.showLoader();
        this.sharedService.baseToImage(result).subscribe({
          next: (res: any) => {
            this.sharedService.hideLoader();
            if (res) {
              this.uploadedAssetObjects[field] = res;
            }
            this.cdRef.detectChanges();
          },
          error: (err) => {
            this.sharedService.hideLoader();
            console.error('baseToImage failed:', err);
          },
        });
      };
      reader.readAsDataURL(file);
    }
  }

  clearAssetImage(field: 'logo_url' | 'logo_dark_url' | 'icon_url', event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedFiles[field] = null;
    delete this.assetErrors[field];
    this.uploadedAssetObjects[field] = null;
    this.originalBrandAssets[field] = null;
    this.brandForm.patchValue({ [field]: '' });
    this.brandForm.get(field)?.markAsDirty();
    this.brandForm.get(field)?.markAsTouched();
    this.cdRef.detectChanges();
  }

  verifyingDomainId: number | string | null = null;

  // DNS Details Modal
  dnsModalVisible = false;
  selectedDnsSetup: any = null;

  ngOnInit(): void {
    this.initForms();
    // The preview opens in the brand's default theme and follows it when it changes;
    // "Follow device" leaves whichever palette tab is being edited.
    this.brandForm.get('default_theme')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((theme) => {
        if (theme === 'light' || theme === 'dark') this.colorThemeTab = theme;
      });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.activeTab = params.get('tab') === 'domains' ? 'domains' : 'brands';
    });
    this.loadData();
  }

  selectTab(tab: 'brands' | 'domains'): void {
    this.activeTab = tab;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge'
    });
  }

  initForms(): void {
    this.brandForm = this.fb.group({
      // \S: a name of only spaces counts as empty.
      name: ['', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(100)]],
      description: [''],
      is_default: [false],
      is_active: [true],
      // Colors Light
      primary_color: ['#FCBA03', Validators.required],
      secondary_color: ['#122331'],
      accent_color: ['#FCBA03'],
      heading_color: ['#122331'],
      text_color: ['#122331'],
      bg_color: ['#F7F6F9'],
      surface_color: ['#FFFFFF'],
      text_muted_color: ['#6B7280'],
      success_color: ['#1F9254'],
      warning_color: ['#D97706'],
      error_color: ['#EF233C'],
      // brand.colors.task — the web app's --b-task-l: the "levels" pill and the task badge circles
      task_color: ['#FFF6D9'],
      // brand.colors.completed — the web app's --b-completed-l: marks a finished task
      completed_color: ['#1F9254'],
      gradient_start: ['#FFEAB0'],
      gradient_end: ['#FFFFFF'],
      // Colors Dark
      dark_primary_color: ['#FCBA03'],
      dark_secondary_color: ['#FFD451'],
      dark_accent_color: ['#FCBA03'],
      dark_heading_color: ['#FFFFFF'],
      dark_text_color: ['#CBD5E1'],
      dark_bg_color: ['#0D1620'],
      dark_surface_color: ['#152331'],
      dark_text_muted_color: ['#9CA3AF'],
      dark_success_color: ['#34D399'],
      dark_warning_color: ['#F59E0B'],
      dark_error_color: ['#FF5A6E'],
      dark_task_color: ['#3A2B04'],
      dark_completed_color: ['#34D399'],
      dark_gradient_start: ['#4A3505'],
      dark_gradient_end: ['#0D1620'],
      // Typography
      font_source: ['google'],
      font_family: ['Inter'],
      font_family_ar: ['Vazirmatn'],
      google_family: ['Inter:wght@300;400;500;600;700;800'],
      base_size: [16, brandNumberValidators('base_size')],
      // Shape
      radius: [12, brandNumberValidators('radius')],
      button_radius: [12, brandNumberValidators('button_radius')],
      shell_max: [400, brandNumberValidators('shell_max')],
      // Defaults
      default_theme: ['system'],
      default_language: ['en'],
      // Identity & Meta
      display_name: [''],
      display_name_ar: [''],
      tagline: [''],
      tagline_ar: [''],
      title: [''],
      title_ar: [''],
      meta_description: [''],
      meta_description_ar: [''],
      logo_url: [''],
      logo_dark_url: [''],
      icon_url: [''],
      // Support & Social
      support_email: ['', [Validators.email]],
      support_phone: [''],
      support_whatsapp: [''],
      support_website: ['', webUrlValidator()],
      social_instagram: ['', webUrlValidator(SOCIAL_LINK_HOSTS['social_instagram'])],
      social_x: ['', webUrlValidator(SOCIAL_LINK_HOSTS['social_x'])],
      social_youtube: ['', webUrlValidator(SOCIAL_LINK_HOSTS['social_youtube'])],
      social_tiktok: ['', webUrlValidator(SOCIAL_LINK_HOSTS['social_tiktok'])],
      social_snapchat: ['', webUrlValidator(SOCIAL_LINK_HOSTS['social_snapchat'])],
      social_linkedin: ['', webUrlValidator(SOCIAL_LINK_HOSTS['social_linkedin'])],
      powered_by: [true],
      terms_url: ['', webUrlValidator()],
      privacy_url: ['', webUrlValidator()],
    });

    this.domainForm = this.fb.group({
      hostname: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9][a-zA-Z0-9-_.]{1,253}\.[a-zA-Z]{2,}$/)]],
      branding_id: [null, [Validators.required]],
      is_primary: [false],
      notes: [''],
      zero_storage: [false],
      // Required, and at least 16 characters, only while the mode is on. The
      // floor mirrors domain/create, which refuses anything shorter — see
      // syncZeroStorageKeyValidators().
      zero_storage_key: [''],
    });

    // The form lives exactly as long as the component, so the subscription ends with it.
    this.domainForm.get('zero_storage')!.valueChanges
      .subscribe((on: boolean) => this.syncZeroStorageKeyValidators(!!on));
  }

  /**
   * Keep the key control's rules in step with the toggle.
   *
   * domain/create refuses a key under 16 characters, so the same floor is
   * applied here — a client should learn that from the field, not from a
   * round trip. Switching the mode off clears the value as well as the rules,
   * so a key typed and then abandoned is never sent.
   */
  private syncZeroStorageKeyValidators(enabled: boolean): void {
    const key = this.domainForm.get('zero_storage_key')!;
    if (enabled) {
      key.setValidators([Validators.required, Validators.minLength(16), (control) => {
        const value = String(control.value || '').trim();
        return value.length >= 16 ? null : { minlength: { requiredLength: 16, actualLength: value.length } };
      }]);
    } else {
      key.clearValidators();
      key.setValue('', { emitEvent: false });
    }
    key.updateValueAndValidity({ emitEvent: false });
  }

  loadData(): void {
    this.isLoading = true;
    this.brandingService.getBrands('', true).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const brandList = Array.isArray(res)
          ? res
          : (res?.items || res?.data?.items || res?.data || res?.result || []);

        this.brands = brandList.map((item: any) => {
          let brandDoc = item.brand;
          if (typeof brandDoc === 'string') {
            try {
              brandDoc = JSON.parse(brandDoc);
            } catch (e) {
              brandDoc = {};
            }
          }
          brandDoc = brandDoc || {};

          const itemLogo = item.logo !== undefined ? item.logo : (brandDoc?.assets?.logo ?? null);
          const itemLogoDark = item.logo_dark !== undefined ? item.logo_dark : (brandDoc?.assets?.logo_dark ?? null);
          const itemIcon = item.icon !== undefined ? item.icon : (brandDoc?.assets?.icon ?? null);

          return {
            ...item,
            name: item.name || 'Brand',
            display_name: item.display_name || brandDoc?.identity?.display_name || '',
            description: item.description || brandDoc?.meta?.description || brandDoc?.identity?.description || '',
            primary: this.ensureHex6(item.primary || brandDoc?.colors?.primary, '#FCBA03'),
            secondary: this.ensureHex6(item.secondary || brandDoc?.colors?.secondary, '#122331'),
            logo: itemLogo,
            logo_dark: itemLogoDark,
            icon: itemIcon,
            brand: {
              ...brandDoc,
              assets: {
                ...(brandDoc?.assets || {}),
                logo: itemLogo,
                logo_dark: itemLogoDark,
                icon: itemIcon,
              }
            }
          };
        });

        // Auto-select first brand for domain form if not set
        if (this.brands.length > 0 && !this.domainForm.get('branding_id')?.value) {
          const defaultBrand = this.brands.find((b) => b.is_default) || this.brands[0];
          this.domainForm.patchValue({ branding_id: defaultBrand.id });
        }
        this.cdRef.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to load brands:', err);
        this.sharedService.createNotification('error', err?.error?.message || this.translate.instant('branding.load-failed'));
        this.cdRef.detectChanges();
      },
    });

    // Same message as the brands call: when both fail together the duplicate is dropped,
    // so the page opens with one error instead of two stacked ones.
    this.loadDomains('branding.load-failed');
  }

  loadDomains(failMessageKey = 'branding.domains-load-failed'): void {
    this.brandingService.getDomains().subscribe({
      next: (res: any) => {
        this.domains = Array.isArray(res)
          ? res
          : (res?.items || res?.data?.items || res?.data || res?.result || []);
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load domains:', err);
        this.sharedService.createNotification('error', err?.error?.message || this.translate.instant(failMessageKey));
      },
    });
  }

  // --- BRAND ACTIONS ---
  openCreateBrandModal(): void {
    this.editingBrand = null;
    this.selectedFiles = {};
    this.uploadedAssetObjects = {};
    this.originalBrandAssets = {};
    this.assetErrors = {};
    this.colorThemeTab = 'light';
    this.brandForm.reset({
      name: '',
      description: '',
      is_default: false,
      is_active: true,
      primary_color: '#FCBA03',
      secondary_color: '#122331',
      accent_color: '#FCBA03',
      heading_color: '#122331',
      text_color: '#122331',
      bg_color: '#F7F6F9',
      surface_color: '#FFFFFF',
      text_muted_color: '#6B7280',
      success_color: '#1F9254',
      warning_color: '#D97706',
      error_color: '#EF233C',
      task_color: '#FFF6D9',
      completed_color: '#1F9254',
      gradient_start: '#FFEAB0',
      gradient_end: '#FFFFFF',
      dark_primary_color: '#FCBA03',
      dark_secondary_color: '#FFD451',
      dark_accent_color: '#FCBA03',
      dark_heading_color: '#FFFFFF',
      dark_text_color: '#CBD5E1',
      dark_bg_color: '#0D1620',
      dark_surface_color: '#152331',
      dark_text_muted_color: '#9CA3AF',
      dark_success_color: '#34D399',
      dark_warning_color: '#F59E0B',
      dark_error_color: '#FF5A6E',
      dark_task_color: '#3A2B04',
      dark_completed_color: '#34D399',
      dark_gradient_start: '#4A3505',
      dark_gradient_end: '#0D1620',
      font_source: 'google',
      font_family: 'Inter',
      font_family_ar: 'Vazirmatn',
      google_family: 'Inter:wght@300;400;500;600;700;800',
      base_size: 16,
      radius: 12,
      button_radius: 12,
      shell_max: 400,
      default_theme: 'system',
      default_language: 'en',
      display_name: '',
      display_name_ar: '',
      tagline: '',
      tagline_ar: '',
      title: '',
      title_ar: '',
      meta_description: '',
      meta_description_ar: '',
      logo_url: '',
      logo_dark_url: '',
      icon_url: '',
      support_email: '',
      support_phone: '',
      support_whatsapp: '',
      support_website: '',
      social_instagram: '',
      social_x: '',
      social_youtube: '',
      social_tiktok: '',
      social_snapchat: '',
      social_linkedin: '',
      powered_by: true,
      terms_url: '',
      privacy_url: '',
    });
    this.isBrandModalVisible = true;
  }

  ensureHex6(color: any, fallback: string = '#FCBA03'): string {
    if (!color || typeof color !== 'string') return fallback;
    let c = color.trim();
    if (!c) return fallback;
    if (!c.startsWith('#')) c = '#' + c;
    if (/^#[0-9a-fA-F]{3}$/.test(c)) {
      c = '#' + c[1] + c[1] + c[2] + c[2] + c[3] + c[3];
    }
    if (/^#[0-9a-fA-F]{8}$/.test(c)) {
      c = c.substring(0, 7);
    }
    if (/^#[0-9a-fA-F]{6}$/.test(c)) {
      return c.toUpperCase();
    }
    return fallback;
  }

  getBrandThemeColor(brand: any, type: 'primary' | 'secondary' | 'accent' | 'heading' | 'bg' | 'surface'): string {
    const fallbacks: Record<string, string> = {
      primary: '#FCBA03',
      secondary: '#122331',
      accent: '#FCBA03',
      heading: '#122331',
      bg: '#F7F6F9',
      surface: '#FFFFFF',
    };
    if (!brand) return fallbacks[type] || '#FCBA03';
    let b = brand.brand;
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};
    const colors = b.colors || brand.colors || {};
    switch (type) {
      case 'primary':
        return this.ensureHex6(brand.primary || brand.primary_color || colors.primary, fallbacks['primary']);
      case 'secondary':
        return this.ensureHex6(brand.secondary || brand.secondary_color || colors.secondary, fallbacks['secondary']);
      case 'accent':
        return this.ensureHex6(brand.accent || brand.accent_color || colors.accent || colors.primary || brand.primary, fallbacks['accent']);
      case 'heading':
        return this.ensureHex6(brand.heading || brand.heading_color || colors.heading || colors.text, fallbacks['heading']);
      case 'bg':
        return this.ensureHex6(brand.bg || brand.bg_color || colors.bg || colors.background, fallbacks['bg']);
      case 'surface':
        return this.ensureHex6(brand.surface || brand.surface_color || colors.surface || colors.card || colors.sheet, fallbacks['surface']);
      default:
        return fallbacks[type] || '#FCBA03';
    }
  }

  onColorHexInput(controlName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value?.trim() || '';
    if (val && !val.startsWith('#') && /^[0-9a-fA-F]{1,6}$/.test(val)) {
      val = '#' + val;
    }
    if (/^#[0-9a-fA-F]{6}$/i.test(val)) {
      this.brandForm.get(controlName)?.setValue(val.toUpperCase(), { emitEvent: true });
    }
  }

  /**
   * The swatch and its hex box share one control, and a control does not echo a view's change
   * back to its other views — so the hex box stayed stale until save. Writing the value back
   * through the control updates both.
   */
  onColorPick(controlName: string, event: Event): void {
    const value = String((event.target as HTMLInputElement).value || '').toUpperCase();
    if (/^#[0-9A-F]{6}$/.test(value)) {
      this.brandForm.get(controlName)?.setValue(value);
    }
  }

  /** The inline message for a typography & shape number that is empty, fractional or out of range. */
  numberError(field: keyof typeof BRAND_NUMBER_LIMITS): string {
    const control = this.brandForm.get(field);
    if (!control || control.valid || !control.touched) return '';
    const [min, max] = BRAND_NUMBER_LIMITS[field];
    return this.translate.instant('branding.number-range', { min, max });
  }

  /** The inline message for a link field, or '' when it is fine (or not touched yet). */
  urlError(field: string): string {
    const control = this.brandForm.get(field);
    if (!control || control.valid || !control.touched) return '';
    const site = control.errors?.['urlSite']?.site;
    return site
      ? this.translate.instant('branding.url-wrong-site', { site })
      : this.translate.instant('branding.url-invalid');
  }

  /** LIVE only once the DNS has been verified; until then the domain reads PENDING DNS. */
  domainStatus(dom: DomainItem): 'live' | 'pending' | 'failed' {
    if (dom.status === 'failed') return 'failed';
    return dom.status === 'live' && !!dom['verified_at'] ? 'live' : 'pending';
  }

  onColorHexBlur(controlName: string): void {
    const current = this.brandForm.get(controlName)?.value;
    const fixed = this.ensureHex6(current, '#FCBA03');
    this.brandForm.get(controlName)?.setValue(fixed, { emitEvent: true });
  }

  getBrandAvatarLogo(brand: any): string {
    if (!brand) return '';
    let b = brand.brand;
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};

    return (
      this.extractUrl(brand.logo) ||
      this.extractUrl(brand.logo_dark) ||
      this.extractUrl(b.assets?.logo) ||
      this.extractUrl(b.assets?.logo_dark) ||
      this.extractUrl(brand.icon) ||
      this.extractUrl(b.assets?.icon) ||
      ''
    );
  }

  extractUrl(asset: any): string {
    if (!asset) return '';

    // Handle Array
    if (Array.isArray(asset) && asset.length > 0) {
      return this.extractUrl(asset[0]);
    }

    // Handle Object
    if (typeof asset === 'object' && asset !== null) {
      if (asset.url && typeof asset.url === 'string') return this.extractUrl(asset.url);
      if (asset.path && typeof asset.path === 'string') return this.extractUrl(asset.path);
      return '';
    }

    // Handle String
    if (typeof asset === 'string') {
      let trimmed = asset.trim();
      if (!trimmed) return '';

      // If it's a JSON stringified array or object
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed) return this.extractUrl(parsed);
        } catch (e) {
          // ignore
        }
      }

      // If multiple http(s):// are concatenated (e.g. https://domainhttps://domain/vault/...)
      const lastHttpIdx = trimmed.lastIndexOf('http://');
      const lastHttpsIdx = trimmed.lastIndexOf('https://');
      const lastIdx = Math.max(lastHttpIdx, lastHttpsIdx);
      if (lastIdx > 0) {
        trimmed = trimmed.substring(lastIdx);
      }

      // A bare /vault/ path belonged to the removed Xano file store — nothing to show.
      if (trimmed.startsWith('/vault/')) {
        return '';
      }

      return trimmed;
    }

    return '';
  }

  cleanAssetForPayload(asset: any): any {
    if (!asset) return null;

    if (Array.isArray(asset)) {
      if (asset.length === 0) return null;
      return this.cleanAssetForPayload(asset[0]);
    }

    if (typeof asset === 'string') {
      const trimmed = asset.trim();
      if (!trimmed) return null;
      let path = trimmed;
      const vaultIdx = trimmed.lastIndexOf('/vault/');
      if (vaultIdx !== -1) {
        path = trimmed.substring(vaultIdx);
      }
      const filename = path.split('/').pop() || 'image.png';
      return {
        access: 'public',
        path: path,
        // A hosted image is addressed by its absolute url; a bare path means nothing on its own.
        ...(/^https?:\/\//i.test(path) ? { url: path } : {}),
        name: filename,
        type: 'image',
      };
    }

    if (typeof asset === 'object' && asset !== null) {
      const obj = { ...asset };
      if (obj.path && typeof obj.path === 'string') {
        const vaultIdx = obj.path.lastIndexOf('/vault/');
        if (vaultIdx !== -1) {
          obj.path = obj.path.substring(vaultIdx);
        }
      } else if (obj.url && typeof obj.url === 'string') {
        const vaultIdx = obj.url.lastIndexOf('/vault/');
        if (vaultIdx !== -1) {
          obj.path = obj.url.substring(vaultIdx);
        }
      }
      // The server stores the file object as sent and the app renders its `url`; keep it when the
      // asset came back with only a `path` (an https link).
      if (!obj.url && typeof obj.path === 'string' && /^https?:\/\//i.test(obj.path)) {
        obj.url = obj.path;
      }
      obj.access = obj.access || 'public';
      obj.type = obj.type || 'image';
      return obj;
    }

    return null;
  }

  openEditBrandModal(brand: BrandItem): void {
    this.editingBrand = brand;
    this.selectedFiles = {};
    this.uploadedAssetObjects = {};
    this.assetErrors = {};
    // A "Follow device" brand previews in light; light/dark is set by the patch below.
    this.colorThemeTab = 'light';

    let b = brand.brand;
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};

    // Keep exact original backend assets
    this.originalBrandAssets = {
      logo_url: brand.logo !== undefined ? brand.logo : (b.assets?.logo ?? null),
      logo_dark_url: brand.logo_dark !== undefined ? brand.logo_dark : (b.assets?.logo_dark ?? null),
      icon_url: brand.icon !== undefined ? brand.icon : (b.assets?.icon ?? null),
    };

    const colors = b.colors || {};
    const colorsDark = b.colors_dark || colors.dark || {};
    const typo = b.typography || {};
    const shape = b.shape || {};
    const idn = b.identity || {};
    const meta = b.meta || {};
    const support = b.support || {};
    const social = b.social || {};
    const legal = b.legal || {};
    const defaults = b.defaults || {};

    const logoUrl = this.extractUrl(brand.logo) || this.extractUrl(b.assets?.logo);
    const logoDarkUrl = this.extractUrl(brand.logo_dark) || this.extractUrl(b.assets?.logo_dark);
    const iconUrl = this.extractUrl(brand.icon) || this.extractUrl(b.assets?.icon);

    const desc = brand.description || b.meta?.description || b.identity?.description || b.description || '';

    this.brandForm.patchValue({
      name: brand.name || idn.display_name || '',
      description: desc,
      is_default: brand.is_default || false,
      is_active: brand.is_active ?? true,
      primary_color: this.ensureHex6(colors.primary || brand.primary || brand['primary_color'], '#FCBA03'),
      secondary_color: this.ensureHex6(colors.secondary || brand.secondary || brand['secondary_color'], '#122331'),
      accent_color: this.ensureHex6(colors.accent || brand['accent'] || brand['accent_color'], this.ensureHex6(colors.primary || brand.primary, '#FCBA03')),
      heading_color: this.ensureHex6(colors.heading || brand['heading'] || brand['heading_color'], '#122331'),
      text_color: this.ensureHex6(colors.text || brand['text'] || brand['text_color'], '#122331'),
      bg_color: this.ensureHex6(colors.bg || brand['bg'] || brand['bg_color'] || colors.background, '#F7F6F9'),
      surface_color: this.ensureHex6(colors.surface || colors.card || brand['surface'] || brand['surface_color'], '#FFFFFF'),
      text_muted_color: this.ensureHex6(colors.text_muted, '#6B7280'),
      success_color: this.ensureHex6(colors.success, '#1F9254'),
      warning_color: this.ensureHex6(colors.warning, '#D97706'),
      error_color: this.ensureHex6(colors.error, '#EF233C'),
      task_color: this.ensureHex6(colors.task, '#FFF6D9'),
      // A brand saved without its own completed colour has always used success for it.
      completed_color: this.ensureHex6(colors.completed || colors.success, '#1F9254'),
      gradient_start: this.ensureHex6(colors.gradient_start, '#FFEAB0'),
      gradient_end: this.ensureHex6(colors.gradient_end, '#FFFFFF'),
      dark_primary_color: this.ensureHex6(colorsDark.primary || colors.primary || brand.primary, '#FCBA03'),
      dark_secondary_color: this.ensureHex6(colorsDark.secondary || colors.secondary || brand.secondary, '#FFD451'),
      dark_accent_color: this.ensureHex6(colorsDark.accent || colors.accent, this.ensureHex6(colorsDark.primary || colors.primary, '#FCBA03')),
      dark_heading_color: this.ensureHex6(colorsDark.heading || colors.heading, '#FFFFFF'),
      dark_text_color: this.ensureHex6(colorsDark.text || colors.text, '#CBD5E1'),
      dark_bg_color: this.ensureHex6(colorsDark.bg || colors.bg, '#0D1620'),
      dark_surface_color: this.ensureHex6(colorsDark.surface || colorsDark.card || colors.surface, '#152331'),
      dark_text_muted_color: this.ensureHex6(colorsDark.text_muted, '#9CA3AF'),
      dark_success_color: this.ensureHex6(colorsDark.success, '#34D399'),
      dark_warning_color: this.ensureHex6(colorsDark.warning, '#F59E0B'),
      dark_error_color: this.ensureHex6(colorsDark.error, '#FF5A6E'),
      dark_task_color: this.ensureHex6(colorsDark.task, '#3A2B04'),
      dark_completed_color: this.ensureHex6(colorsDark.completed || colorsDark.success, '#34D399'),
      dark_gradient_start: this.ensureHex6(colorsDark.gradient_start, '#4A3505'),
      dark_gradient_end: this.ensureHex6(colorsDark.gradient_end, '#0D1620'),
      font_source: typo.source || 'google',
      font_family: typo.family || 'Inter',
      font_family_ar: typo.family_ar || 'Vazirmatn',
      google_family: typo.google_family || 'Inter:wght@300;400;500;600;700;800',
      base_size: Number(typo.base_size ?? 16),
      radius: shape.radius ?? 12,
      button_radius: shape.button_radius ?? 12,
      shell_max: shape.shell_max ?? 400,
      default_theme: defaults.theme || 'system',
      default_language: defaults.language || 'en',
      display_name: idn.display_name || brand.name || '',
      display_name_ar: idn.display_name_ar || '',
      tagline: idn.tagline || '',
      tagline_ar: idn.tagline_ar || '',
      title: meta.title || '',
      title_ar: meta.title_ar || '',
      meta_description: meta.description || desc,
      meta_description_ar: meta.description_ar || '',
      logo_url: logoUrl,
      logo_dark_url: logoDarkUrl,
      icon_url: iconUrl,
      support_email: support.email || '',
      support_phone: support.phone || '',
      support_whatsapp: support.whatsapp || '',
      support_website: support.website || '',
      social_instagram: social.instagram || '',
      social_x: social.x || '',
      social_youtube: social.youtube || '',
      social_tiktok: social.tiktok || '',
      social_snapchat: social.snapchat || '',
      social_linkedin: social.linkedin || '',
      powered_by: b.flags?.powered_by ?? true,
      terms_url: legal.terms_url || '',
      privacy_url: legal.privacy_url || '',
    });
    this.isBrandModalVisible = true;
  }

  /* ----------------------------- Live preview helpers ----------------------------- */

  /** Picks the light or dark form value depending on the preview tab. */
  pv(lightControl: string, darkControl: string, fallbackLight: string, fallbackDark: string): string {
    const v = this.brandForm?.value || {};
    return this.colorThemeTab === 'dark'
      ? (v[darkControl] || fallbackDark)
      : (v[lightControl] || fallbackLight);
  }

  /** The web app paints the page in gradient_end and fades gradient_start into it over the top third. */
  get previewGradient(): string {
    return `linear-gradient(180deg, ${this.previewGradientStart} 0%, ${this.previewPageBg} 32%)`;
  }

  get previewGradientStart(): string {
    return this.pv('gradient_start', 'dark_gradient_start', '#FFEAB0', '#4A3505');
  }

  get previewPageBg(): string {
    return this.pv('gradient_end', 'dark_gradient_end', '#FFFFFF', '#0D1620');
  }

  /** A Light or Dark default theme is forced on visitors, so the participant page shows no theme switch. */
  get previewThemeForced(): boolean {
    const theme = this.brandForm?.value?.default_theme;
    return theme === 'light' || theme === 'dark';
  }

  /** The phone's clock and icons sit on gradient_start, so they go light or dark to stay readable on it. */
  get previewStatusBarColor(): string {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(this.previewGradientStart).trim());
    if (!match) return '#122331';
    const n = parseInt(match[1], 16);
    const luminance = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
    return luminance < 0.55 ? '#FFFFFF' : '#122331';
  }

  /**
   * Brand colours the form does not edit (the grey ramp, on_primary) but the web
   * app's entry screen uses: taken from the brand being edited, else the defaults
   * a saved brand gets.
   */
  previewTone(key: 'grey_1' | 'grey_2' | 'grey_3' | 'grey_4' | 'on_primary'): string {
    const defaults = {
      light: { grey_1: '#E5E7EB', grey_2: '#D1D5DB', grey_3: '#9CA3AF', grey_4: '#6B7280', on_primary: '#1D1D1B' },
      dark: { grey_1: '#22303F', grey_2: '#3C4B5C', grey_3: '#6B7A8C', grey_4: '#9CA3AF', on_primary: '#1D1D1B' },
    };
    const doc = this.editingBrandDoc;
    const colors = this.colorThemeTab === 'dark'
      ? (doc.colors_dark || doc.colors?.dark || {})
      : (doc.colors || {});
    return colors[key] || defaults[this.colorThemeTab][key];
  }

  private brandDocCache: { source: any; doc: any } = { source: undefined, doc: {} };

  /** The edited brand's document, parsed once per brand rather than on every change detection. */
  private get editingBrandDoc(): any {
    const source = this.editingBrand?.brand;
    if (source !== this.brandDocCache.source) {
      let doc = source;
      if (typeof doc === 'string') {
        try {
          doc = JSON.parse(doc);
        } catch (e) {
          doc = {};
        }
      }
      this.brandDocCache = { source, doc: doc || {} };
    }
    return this.brandDocCache.doc;
  }

  get previewIsArabic(): boolean {
    return this.brandForm?.value?.default_language === 'ar';
  }

  get previewDir(): 'rtl' | 'ltr' {
    return this.previewIsArabic ? 'rtl' : 'ltr';
  }

  /** The web app's own font stacks, so a family the device lacks falls back the same way. */
  get previewFontFamily(): string {
    const v = this.brandForm?.value || {};
    return this.previewIsArabic
      ? `'${v.font_family_ar || 'Codec Pro'}', 'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif`
      : `'${v.font_family || 'Codec Pro'}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
  }

  /** Inner width of the mock phone screen, in px. */
  readonly previewScreenWidth = 248;

  /** The mock screen against a 400px phone: every real size is drawn at this ratio. */
  private readonly previewScale = 0.62;

  /**
   * The web app sizes its entry screen in fixed px (heading 26, body 14) from a
   * 16px reference, so typography.base_size does not change it; neither may the preview.
   */
  get previewFontSize(): number {
    return Math.round(16 * this.previewScale * 10) / 10;
  }

  /** shell_max scaled into the phone screen, never wider than the screen itself. */
  get previewShellWidth(): number {
    const shell = Number(this.brandForm?.value?.shell_max ?? 400);
    if (!shell || isNaN(shell)) return this.previewScreenWidth;
    return Math.min(this.previewScreenWidth, Math.max(140, Math.round(shell * this.previewScale)));
  }

  /** shape.radius as drawn on the mock screen; the web app uses it for the phone field and the Continue button. */
  get previewRadius(): number {
    const radius = Number(this.brandForm?.value?.radius ?? 12);
    return Math.round((isNaN(radius) ? 12 : radius) * this.previewScale);
  }

  get previewName(): string {
    const v = this.brandForm?.value || {};
    return (this.previewIsArabic ? v.display_name_ar : v.display_name) || v.display_name || v.name || 'Your Brand';
  }

  get previewTitle(): string {
    const v = this.brandForm?.value || {};
    return (this.previewIsArabic ? v.title_ar : v.title) || v.title || `Join the campaign | ${this.previewName}`;
  }

  /**
   * The web app's entry (WhatsApp number) screen, word for word, in the brand's
   * default language rather than the panel's — a participant opening an Arabic
   * brand sees Arabic whatever language the client happens to run the panel in.
   */
  get previewCopy(): Record<string, string> {
    return this.previewIsArabic ? BrandingComponent.PREVIEW_COPY.ar : BrandingComponent.PREVIEW_COPY.en;
  }

  private static readonly PREVIEW_COPY: Record<'en' | 'ar', Record<string, string>> = {
    en: {
      light: 'Light',
      dark: 'Dark',
      otherLanguage: 'العربية',
      headingStart: 'What is your ',
      whatsapp: 'WhatsApp',
      headingEnd: ' number?',
      intro: "We'll send you a one-time code on WhatsApp to verify it. No app download, no account needed.",
      hint: '8 digits after',
      agree: 'I agree to the',
      and: '&',
      terms: 'Terms & Conditions',
      privacy: 'Privacy Policy',
      note: 'By continuing, you agree to receive verification codes and campaign updates via WhatsApp.',
      continue: 'Continue',
    },
    ar: {
      light: 'فاتح',
      dark: 'داكن',
      otherLanguage: 'English',
      headingStart: 'ما هو رقم ',
      whatsapp: 'واتساب',
      headingEnd: ' الخاص بك؟',
      intro: 'سنرسل لك رمزًا لمرة واحدة عبر واتساب للتحقق منه. بدون تحميل تطبيق، وبدون إنشاء حساب.',
      hint: '8 أرقام بعد',
      agree: 'أوافق على',
      and: '&',
      terms: 'الشروط والأحكام',
      privacy: 'سياسة الخصوصية',
      note: 'بالمتابعة، فإنك توافق على تلقي رمز التحقق وتحديثات الحملة عبر واتساب.',
      continue: 'متابعة',
    },
  };

  /** A legal link as the participant would open it; anything that is not http(s) is not linked. */
  legalHref(url: string | null | undefined): string | null {
    const value = String(url || '').trim();
    return /^https?:\/\//i.test(value) ? value : null;
  }

  get previewLogoUrl(): string {
    const v = this.brandForm?.value || {};
    const raw = this.colorThemeTab === 'dark' ? (v.logo_dark_url || v.logo_url) : v.logo_url;
    return this.extractUrl(raw);
  }

  /** Optional text fields go to the backend as null (not '') when the user leaves them empty. */
  private orNull(value: any): string | null {
    if (value === undefined || value === null) return null;
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : null;
  }

  buildCompleteBrandDocument(val: any, existingBrand?: any): any {
    let b = existingBrand?.brand || {};
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};

    const colors = b.colors || {};
    const colorsDark = b.colors_dark || colors.dark || {};
    const typo = b.typography || {};
    const shape = b.shape || {};
    const idn = b.identity || {};
    const meta = b.meta || {};
    const support = b.support || {};
    const social = b.social || {};
    const legal = b.legal || {};
    const flags = b.flags || {};
    const defaults = b.defaults || {};

    const primHex = this.ensureHex6(val?.primary_color || colors.primary || existingBrand?.primary, '#FCBA03');
    const secHex = this.ensureHex6(val?.secondary_color || colors.secondary || existingBrand?.secondary, '#122331');
    const accHex = this.ensureHex6(val?.accent_color || colors.accent, primHex);
    const headHex = this.ensureHex6(val?.heading_color || colors.heading, '#122331');
    const textHex = this.ensureHex6(val?.text_color || colors.text, '#122331');
    const bgHex = this.ensureHex6(val?.bg_color || colors.bg, '#F7F6F9');
    const surfHex = this.ensureHex6(val?.surface_color || colors.surface || colors.card, '#FFFFFF');
    const textMutedHex = this.ensureHex6(val?.text_muted_color || colors.text_muted, '#6B7280');
    const successHex = this.ensureHex6(val?.success_color || colors.success, '#1F9254');
    const warningHex = this.ensureHex6(val?.warning_color || colors.warning, '#D97706');
    const errorHex = this.ensureHex6(val?.error_color || colors.error, '#EF233C');
    const taskHex = this.ensureHex6(val?.task_color || colors.task, '#FFF6D9');
    const completedHex = this.ensureHex6(val?.completed_color || colors.completed, successHex);
    const gradStartHex = this.ensureHex6(val?.gradient_start || colors.gradient_start, '#FFEAB0');
    const gradEndHex = this.ensureHex6(val?.gradient_end || colors.gradient_end, '#FFFFFF');

    const darkPrimHex = this.ensureHex6(val?.dark_primary_color || colorsDark.primary || colors.primary, primHex);
    const darkSecHex = this.ensureHex6(val?.dark_secondary_color || colorsDark.secondary || colors.secondary, '#FFD451');
    const darkAccHex = this.ensureHex6(val?.dark_accent_color || colorsDark.accent, accHex);
    const darkHeadHex = this.ensureHex6(val?.dark_heading_color || colorsDark.heading, '#FFFFFF');
    const darkTextHex = this.ensureHex6(val?.dark_text_color || colorsDark.text, '#CBD5E1');
    const darkBgHex = this.ensureHex6(val?.dark_bg_color || colorsDark.bg, '#0D1620');
    const darkSurfHex = this.ensureHex6(val?.dark_surface_color || colorsDark.surface || colorsDark.card, '#152331');
    const darkTextMutedHex = this.ensureHex6(val?.dark_text_muted_color || colorsDark.text_muted, '#9CA3AF');
    const darkSuccessHex = this.ensureHex6(val?.dark_success_color || colorsDark.success, '#34D399');
    const darkWarningHex = this.ensureHex6(val?.dark_warning_color || colorsDark.warning, '#F59E0B');
    const darkErrorHex = this.ensureHex6(val?.dark_error_color || colorsDark.error, '#FF5A6E');
    const darkTaskHex = this.ensureHex6(val?.dark_task_color || colorsDark.task, '#3A2B04');
    const darkCompletedHex = this.ensureHex6(val?.dark_completed_color || colorsDark.completed, darkSuccessHex);
    const darkGradStartHex = this.ensureHex6(val?.dark_gradient_start || colorsDark.gradient_start, '#4A3505');
    const darkGradEndHex = this.ensureHex6(val?.dark_gradient_end || colorsDark.gradient_end, '#0D1620');

    const displayName = val?.display_name || idn.display_name || val?.name || existingBrand?.name || 'Brand';
    const displayNameAr = val?.display_name_ar || idn.display_name_ar || '';
    const desc = val?.description || val?.meta_description || meta.description || idn.description || existingBrand?.description || (displayName ? `Join ${displayName} campaigns straight from your browser. Scan a QR code, play and win.` : '');
    const descAr = val?.meta_description_ar || meta.description_ar || idn.description_ar || (displayNameAr ? `انضم إلى حملات ${displayNameAr} مباشرة من متصفحك. امسح رمز الاستجابة السريعة، العب واربح.` : 'مباشرة من متصفحك. امسح رمز الاستجابة السريعة، العب واربح انضم إلى حملات');

    const logoAsset = (this.uploadedAssetObjects?.['logo_url'] ? this.cleanAssetForPayload(this.uploadedAssetObjects['logo_url']) : null) || existingBrand?.logo || b.assets?.logo || (val?.logo_url ? this.cleanAssetForPayload(val.logo_url) : null);
    const logoDarkAsset = (this.uploadedAssetObjects?.['logo_dark_url'] ? this.cleanAssetForPayload(this.uploadedAssetObjects['logo_dark_url']) : null) || existingBrand?.logo_dark || b.assets?.logo_dark || (val?.logo_dark_url ? this.cleanAssetForPayload(val.logo_dark_url) : null);
    const iconAsset = (this.uploadedAssetObjects?.['icon_url'] ? this.cleanAssetForPayload(this.uploadedAssetObjects['icon_url']) : null) || existingBrand?.icon || b.assets?.icon || (val?.icon_url ? this.cleanAssetForPayload(val.icon_url) : null);
    const ogImageAsset = existingBrand?.og_image || b.assets?.og_image || null;

    const darkColorsMap = {
      primary: darkPrimHex,
      secondary: darkSecHex,
      accent: darkAccHex,
      bg: darkBgHex,
      card: darkSurfHex,
      surface: darkSurfHex,
      surface_alt: colorsDark.surface_alt || '#1E3040',
      sheet: darkSurfHex,
      heading: darkHeadHex,
      text: darkTextHex,
      text_muted: darkTextMutedHex,
      on_primary: colorsDark.on_primary || '#1D1D1B',
      card_border: colorsDark.card_border || '#24384A',
      card_alt: colorsDark.card_alt || '#101C27',
      gradient_start: darkGradStartHex,
      gradient_end: darkGradEndHex,
      task: darkTaskHex,
      success: darkSuccessHex,
      warning: darkWarningHex,
      error: darkErrorHex,
      completed: darkCompletedHex,
      disabled: colorsDark.disabled || '#22303F',
      grey_1: colorsDark.grey_1 || '#22303F',
      grey_2: colorsDark.grey_2 || '#3C4B5C',
      grey_3: colorsDark.grey_3 || '#6B7A8C',
      grey_4: colorsDark.grey_4 || '#9CA3AF',
      grey_5: colorsDark.grey_5 || '#C0C4CB',
      grey_6: colorsDark.grey_6 || '#E5E7EB',
    };

    return {
      meta: {
        title: val?.title || meta.title || (displayName ? `Join the campaign | ${displayName}` : ''),
        title_ar: val?.title_ar || meta.title_ar || (displayNameAr ? `انضم إلى الحملة | ${displayNameAr}` : ''),
        description: desc,
        description_ar: descAr,
      },
      flags: {
        powered_by: val?.powered_by !== undefined ? !!val.powered_by : (flags.powered_by !== undefined ? !!flags.powered_by : true),
      },
      legal: {
        terms_url: this.orNull(val?.terms_url ?? legal.terms_url),
        privacy_url: this.orNull(val?.privacy_url ?? legal.privacy_url),
      },
      shape: {
        radius: Number(val?.radius ?? shape.radius ?? 12),
        radius_lg: Number(val?.radius ? Number(val.radius) * 2 : (shape.radius_lg ?? 24)),
        button_radius: Number(val?.button_radius ?? shape.button_radius ?? 12),
        shell_max: Number(val?.shell_max ?? shape.shell_max ?? 400),
      },
      colors: {
        primary: primHex,
        secondary: secHex,
        accent: accHex,
        bg: bgHex,
        card: surfHex,
        surface: surfHex,
        surface_alt: colors.surface_alt || '#EBEBEB',
        sheet: surfHex,
        heading: headHex,
        text: textHex,
        text_muted: textMutedHex,
        on_primary: colors.on_primary || '#1D1D1B',
        card_border: colors.card_border || '#EBEBEB',
        card_alt: colors.card_alt || '#FAFAFA',
        gradient_start: gradStartHex,
        gradient_end: gradEndHex,
        task: taskHex,
        success: successHex,
        warning: warningHex,
        error: errorHex,
        completed: completedHex,
        disabled: colors.disabled || '#F1F1F1',
        grey_1: colors.grey_1 || '#E5E7EB',
        grey_2: colors.grey_2 || '#D1D5DB',
        grey_3: colors.grey_3 || '#9CA3AF',
        grey_4: colors.grey_4 || '#6B7280',
        grey_5: colors.grey_5 || '#3B475C',
        grey_6: colors.grey_6 || '#122331',
      },
      colors_dark: darkColorsMap,
      social: {
        x: this.orNull(val?.social_x ?? social.x),
        instagram: this.orNull(val?.social_instagram ?? social.instagram),
        tiktok: this.orNull(val?.social_tiktok ?? social.tiktok),
        snapchat: this.orNull(val?.social_snapchat ?? social.snapchat),
        youtube: this.orNull(val?.social_youtube ?? social.youtube),
        linkedin: this.orNull(val?.social_linkedin ?? social.linkedin),
      },
      support: {
        email: this.orNull(val?.support_email ?? support.email),
        phone: this.orNull(val?.support_phone ?? support.phone),
        whatsapp: this.orNull(val?.support_whatsapp ?? support.whatsapp),
        website: this.orNull(val?.support_website ?? support.website),
      },
      identity: {
        display_name: displayName,
        display_name_ar: displayNameAr,
        tagline: val?.tagline || idn.tagline || '',
        tagline_ar: val?.tagline_ar || idn.tagline_ar || '',
        description: desc,
        description_ar: descAr,
      },
      typography: {
        source: val?.font_source || typo.source || 'google',
        family: val?.font_family || typo.family || 'Inter',
        family_ar: val?.font_family_ar || typo.family_ar || 'Vazirmatn',
        heading_family: typo.heading_family || val?.font_family || 'Inter',
        google_family: val?.font_source === 'google' ? (val?.google_family || typo.google_family || `${val?.font_family || 'Inter'}:wght@300;400;500;600;700;800`) : 'Inter:wght@300;400;500;600;700;800',
        google_family_ar: `${val?.font_family_ar || typo.family_ar || 'Vazirmatn'}:wght@300;400;500;600;700;800`,
        fallback: typo.fallback || 'sans-serif',
        base_size: Number(val?.base_size ?? typo.base_size ?? 16),
      },
      defaults: {
        theme: val?.default_theme || defaults.theme || 'system',
        language: val?.default_language || defaults.language || 'en',
      },
      assets: {
        logo: logoAsset,
        logo_dark: logoDarkAsset,
        icon: iconAsset,
        og_image: ogImageAsset,
      },
    };
  }

  buildFullBrandJson(val: any, existingBrand?: any): any {
    const brandDoc = this.buildCompleteBrandDocument(val, existingBrand);
    const name = val?.name || existingBrand?.name || brandDoc.identity?.display_name || 'Brand';
    const desc = val?.description !== undefined ? val.description : (existingBrand?.description !== undefined ? existingBrand.description : (brandDoc.meta?.description || ''));
    const isActive = val?.is_active !== undefined ? !!val.is_active : (existingBrand?.is_active !== undefined ? !!existingBrand.is_active : true);
    const isDefault = val?.is_default !== undefined ? !!val.is_default : (existingBrand?.is_default !== undefined ? !!existingBrand.is_default : false);

    return {
      name: name,
      description: desc,
      is_active: isActive,
      is_default: isDefault,
      brand: brandDoc,
    };
  }

  saveBrand(): void {
    if (this.brandForm.invalid) {
      // Inline messages only — a notification here would close this modal (closeAll) and
      // throw away everything typed into it. Take the user to the first bad field instead.
      // A JSON import saves without the form open, so open it to show what needs fixing.
      this.isBrandModalVisible = true;
      this.brandForm.markAllAsTouched();
      this.cdRef.detectChanges();
      setTimeout(() => {
        const firstInvalid = document.querySelector<HTMLElement>('.brand-customizer-form [formcontrolname].ng-invalid');
        firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid?.focus({ preventScroll: true });
      });
      return;
    }

    const val = this.brandForm.value;

    const prepareAssetForPayload = (field: 'logo_url' | 'logo_dark_url' | 'icon_url') => {
      const formVal = this.brandForm.get(field)?.value;
      if (!formVal) return null;

      // 1. If a new file was uploaded by user in this modal session -> send clean baseToImage response
      if (this.uploadedAssetObjects[field]) {
        return this.cleanAssetForPayload(this.uploadedAssetObjects[field]);
      }

      // 2. If editing existing brand and no new file was chosen -> send clean original backend asset
      if (this.editingBrand) {
        const orig = this.originalBrandAssets[field];
        if (orig !== undefined && orig !== null && orig !== '') {
          return this.cleanAssetForPayload(orig);
        }
      }

      return null;
    };

    const logoPayload = prepareAssetForPayload('logo_url');
    const logoDarkPayload = prepareAssetForPayload('logo_dark_url');
    const iconPayload = prepareAssetForPayload('icon_url');

    // Assemble the complete Brand document
    const brandDoc = this.buildCompleteBrandDocument(val, this.editingBrand);

    this.isSavingBrand = true;

    const parseErrorMessage = (err: any, fallback: string): string => {
      if (!err) return fallback;
      console.error(fallback, err);
      if (err.error) {
        if (typeof err.error === 'string') return err.error;
        if (err.error.message && typeof err.error.message === 'string') return err.error.message;
        if (err.error.error) return typeof err.error.error === 'string' ? err.error.error : JSON.stringify(err.error.error);
        if (err.error.detail) return typeof err.error.detail === 'string' ? err.error.detail : JSON.stringify(err.error.detail);
        if (err.error.errors) return typeof err.error.errors === 'string' ? err.error.errors : JSON.stringify(err.error.errors);
        try {
          return JSON.stringify(err.error);
        } catch (e) {
          // ignore stringify error
        }
      }
      return err.message || fallback;
    };

    if (this.editingBrand) {
      // A uuid — Number() would turn it into NaN, which JSON writes as null.
      const targetId = this.editingBrand.id || (this.editingBrand as any)._id || (this.editingBrand as any).branding_id;
      const payload: any = {
        id: targetId,
        name: val.name || 'Brand',
        brand: brandDoc,
        is_active: val.is_active ?? true,
        logo: logoPayload,
        logo_dark: logoDarkPayload,
        icon: iconPayload,
      };

      this.brandingService.updateBrand(payload).subscribe({
        next: (res: any) => {
          this.isSavingBrand = false;
          this.isBrandModalVisible = false;
          this.sharedService.showSuccess('Brand updated successfully');

          // Immediately mutate local state and trigger CD for instant UI refresh
          const responseBrand = res?.brand || res?.data?.brand || {};
          const mergedBrandDoc = {
            ...brandDoc,
            ...responseBrand,
            assets: {
              logo: logoPayload !== undefined ? logoPayload : (responseBrand.assets?.logo ?? brandDoc.assets?.logo ?? null),
              logo_dark: logoDarkPayload !== undefined ? logoDarkPayload : (responseBrand.assets?.logo_dark ?? brandDoc.assets?.logo_dark ?? null),
              icon: iconPayload !== undefined ? iconPayload : (responseBrand.assets?.icon ?? brandDoc.assets?.icon ?? null),
              og_image: responseBrand.assets?.og_image ?? brandDoc.assets?.og_image ?? null,
            },
          };

          if (this.editingBrand) {
            const idx = this.brands.findIndex((b) => b.id === targetId || (b as any)._id === targetId);
            if (idx !== -1) {
              this.brands[idx] = {
                ...this.brands[idx],
                name: val.name || this.brands[idx].name,
                description: val.description,
                primary: brandDoc.colors.primary,
                secondary: brandDoc.colors.secondary,
                brand: mergedBrandDoc,
                is_active: val.is_active ?? this.brands[idx].is_active,
                logo: logoPayload !== undefined ? logoPayload : (res?.logo ?? null),
                logo_dark: logoDarkPayload !== undefined ? logoDarkPayload : (res?.logo_dark ?? null),
                icon: iconPayload !== undefined ? iconPayload : (res?.icon ?? null),
              };
              this.brands = [...this.brands];
              this.cdRef.detectChanges();
            }
          }
          this.loadData();
        },
        error: (err) => {
          this.isSavingBrand = false;
          const errMsg = parseErrorMessage(err, 'Failed to update brand');
          this.sharedService.createNotification('error', errMsg);
        },
      });
    } else {
      const payload: any = {
        name: val.name || 'Brand',
        brand: brandDoc,
        is_default: val.is_default || false,
        is_active: true,
        logo: logoPayload,
        logo_dark: logoDarkPayload,
        icon: iconPayload,
      };

      this.brandingService.createBrand(payload).subscribe({
        next: (res: any) => {
          this.isSavingBrand = false;
          this.isBrandModalVisible = false;
          this.sharedService.showSuccess('Brand created successfully');

          const responseBrand = res?.brand || res?.data?.brand || {};
          const mergedBrandDoc = {
            ...brandDoc,
            ...responseBrand,
            assets: {
              logo: logoPayload ?? responseBrand.assets?.logo ?? brandDoc.assets?.logo ?? null,
              logo_dark: logoDarkPayload ?? responseBrand.assets?.logo_dark ?? brandDoc.assets?.logo_dark ?? null,
              icon: iconPayload ?? responseBrand.assets?.icon ?? brandDoc.assets?.icon ?? null,
              og_image: responseBrand.assets?.og_image ?? brandDoc.assets?.og_image ?? null,
            },
          };

          const newBrandItem: BrandItem = {
            id: res?.id || res?.data?.id || Date.now(),
            name: val.name || 'Brand',
            description: val.description || '',
            primary: brandDoc.colors.primary,
            secondary: brandDoc.colors.secondary,
            is_default: val.is_default || false,
            is_active: true,
            brand: mergedBrandDoc,
            logo: logoPayload || (res?.logo || null),
            logo_dark: logoDarkPayload || (res?.logo_dark || null),
            icon: iconPayload || (res?.icon || null),
          };
          this.brands = [newBrandItem, ...this.brands];
          this.cdRef.detectChanges();
          this.loadData();
        },
        error: (err) => {
          this.isSavingBrand = false;
          const errMsg = parseErrorMessage(err, 'Failed to create brand');
          this.sharedService.createNotification('error', errMsg);
        },
      });
    }
  }

  setDefaultBrand(brand: BrandItem): void {
    if (brand.is_default) return;
    this.brandingService.setDefaultBrand(brand.id).subscribe({
      next: () => {
        this.sharedService.showSuccess(`'${brand.name}' is now default brand`);
        this.loadData();
      },
      error: (err) => {
        this.sharedService.createNotification('error', err?.error?.message || 'Failed to set default brand');
      },
    });
  }

  toggleBrandActive(brand: BrandItem): void {
    const newActiveState = !brand.is_active;
    this.brandingService.updateBrand({ id: brand.id, is_active: newActiveState }).subscribe({
      next: () => {
        this.sharedService.showSuccess(`Brand ${newActiveState ? 'activated' : 'deactivated'}`);
        brand.is_active = newActiveState;
      },
      error: (err) => {
        this.sharedService.createNotification('error', err?.error?.message || 'Failed to toggle brand status');
      },
    });
  }

  // --- DOMAIN ACTIONS ---
  openCreateDomainModal(brandId?: number | string): void {
    this.editingDomain = null;
    this.domainForm.get('hostname')?.enable();
    // From the page header there is no brand in context; preselect the default one rather than
    // leaving a required field empty
    const fallback = this.brands.find((b: any) => b.is_default) || (this.brands.length === 1 ? this.brands[0] : null);
    const selectedBrandId = brandId || fallback?.id || null;
    this.domainForm.reset({
      hostname: '',
      branding_id: selectedBrandId,
      is_primary: false,
      notes: '',
      zero_storage: false,
      zero_storage_key: '',
    });
    this.syncZeroStorageKeyValidators(false);
    this.isDomainModalVisible = true;
  }

  openEditDomainModal(domain: DomainItem): void {
    this.editingDomain = domain;
    this.domainForm.reset({
      hostname: domain.hostname,
      branding_id: domain.branding_id || null,
      is_primary: !!domain.is_primary,
      notes: domain.notes || '',
      zero_storage: domain.zero_storage === true,
      zero_storage_key: '',
    });
    this.domainForm.get('hostname')?.disable();
    // Zero storage is set once, at create; an existing domain only shows it read-only.
    this.syncZeroStorageKeyValidators(false);
    this.isDomainModalVisible = true;
  }

  saveDomain(): void {
    if (this.domainForm.invalid) {
      Object.values(this.domainForm.controls).forEach((ctrl) => ctrl.markAsTouched());
      return;
    }

    const val = this.domainForm.value;

    if (this.editingDomain) {
      // domain/update never takes zero_storage or its key — neither can change after create.
      this.isSavingDomain = true;
      this.brandingService.updateDomain({
        id: this.editingDomain.id,
        branding_id: val.branding_id,
        is_primary: val.is_primary,
        notes: val.notes
      }).subscribe({
        next: () => this.onDomainSaved(true),
        error: (err) => {
          this.isSavingDomain = false;
          this.sharedService.createNotification('error', err?.error?.message || this.translate.instant('branding.domain-save-failed'));
        },
      });
      return;
    }

    const zeroStorage = val.zero_storage === true;

    // Both fields are left out entirely when the mode is off, so an abandoned
    // key can never ride along with an ordinary domain.
    const payload = {
      hostname: val.hostname,
      branding_id: val.branding_id,
      is_primary: val.is_primary,
      notes: val.notes,
      ...(zeroStorage
        ? { zero_storage: true, zero_storage_key: String(val.zero_storage_key || '').trim() }
        : {}),
    };

    if (!zeroStorage) {
      this.submitDomain(payload, false);
      return;
    }

    // Three things become permanent the moment this succeeds, so the decision
    // gets the same confirmation the other irreversible actions in the app use.
    this.modal.confirm({
      nzTitle: this.translate.instant('branding.zero-storage-confirm-title'),
      nzContent: this.translate.instant('branding.zero-storage-confirm-content', { hostname: val.hostname }),
      nzOkText: this.translate.instant('branding.zero-storage-confirm-ok'),
      nzCancelText: this.translate.instant('branding.cancel'),
      nzOkDanger: true,
      nzCentered: true,
      // The OK label is a whole sentence, so the 416px default confirm box
      // overflows it; the wider box and the styles.scss rules keep both buttons inside.
      nzWidth: 520,
      nzClassName: 'zero-storage-confirm-modal',
      nzOnOk: () => this.submitDomain(payload, true),
    });
  }

  private submitDomain(payload: any, zeroStorage: boolean): void {
    this.isSavingDomain = true;

    this.brandingService.createDomain(payload).subscribe({
      next: (res: any) => {
        // A refusal answered with success:false must not be reported as a created domain.
        if (res?.success === false) {
          this.isSavingDomain = false;
          this.handleDomainRefusal(res, zeroStorage);
          return;
        }
        this.onDomainSaved(false);
      },
      // MicroApiService re-throws an isSuccess:false answer as { error: { message, code, errors } },
      // so the refusals land here rather than in `next`.
      error: (err) => {
        this.isSavingDomain = false;
        this.handleDomainRefusal(err?.error || {}, zeroStorage);
      },
    });
  }

  private onDomainSaved(isEditing: boolean): void {
    this.isSavingDomain = false;
    this.isDomainModalVisible = false;
    this.sharedService.showSuccess(this.translate.instant(isEditing ? 'branding.domain-updated' : 'branding.domain-added'));
    this.loadDomains();
  }

  /**
   * Tell the two kinds of refusal apart and keep the modal open either way.
   *
   * A hostname refusal arrives with an `errors` list. The environment having no
   * master key arrives as ZERO_STORAGE_UNAVAILABLE — an operations problem, not
   * something they can fix by retyping, so it gets a dialog that says so rather
   * than a red toast. Anything else (domain taken, key too short) is a toast.
   */
  private handleDomainRefusal(res: any, zeroStorage: boolean): void {
    const errors: string[] = Array.isArray(res?.errors) ? res.errors : [];
    if (errors.length > 0) {
      this.sharedService.createNotification('error', errors.join(' '));
      return;
    }

    if (zeroStorage && res?.code === 'ZERO_STORAGE_UNAVAILABLE') {
      this.modal.warning({
        nzTitle: this.translate.instant('branding.zero-storage-unavailable-title'),
        nzContent: `${res?.message || ''} ${this.translate.instant('branding.zero-storage-unavailable-content')}`.trim(),
        nzOkText: this.translate.instant('branding.close'),
        nzCentered: true,
      });
      return;
    }

    this.sharedService.createNotification('error', res?.message || this.translate.instant('branding.domain-save-failed'));
  }

  verifyDomain(domain: DomainItem): void {
    this.verifyingDomainId = domain.id;
    this.brandingService.verifyDomain(domain.id).subscribe({
      next: (res: any) => {
        this.verifyingDomainId = null;
        if (res?.status === 'live' || res?.is_verified) {
          this.sharedService.showSuccess('Domain DNS verified! Status is now LIVE.');
        } else {
          this.sharedService.showSuccess(res?.message || 'Verification check complete. DNS propagation may take up to an hour.');
        }
        this.loadDomains();
      },
      error: (err) => {
        this.verifyingDomainId = null;
        this.sharedService.createNotification('error', err?.error?.message || 'DNS Verification check failed');
      },
    });
  }

  deleteDomain(domain: DomainItem): void {
    if (confirm(`Are you sure you want to remove domain '${domain.hostname}'?`)) {
      this.brandingService.deleteDomain(domain.id).subscribe({
        next: () => {
          this.sharedService.showSuccess('Domain deleted');
          this.loadDomains();
        },
        error: (err) => {
          this.sharedService.createNotification('error', err?.error?.message || 'Failed to delete domain');
        },
      });
    }
  }

  showDnsSetup(domain: DomainItem): void {
    this.selectedDnsSetup = {
      hostname: domain.hostname,
      dns: domain.dns_setup,
    };
    this.dnsModalVisible = true;
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.sharedService.showSuccess('Copied to clipboard');
    });
  }

  getBrandName(brandingId: number | string): string {
    const b = this.brands.find((x) => x.id === brandingId);
    return b ? (b.display_name || b.name) : this.translate.instant('branding.unassigned-brand');
  }

  getBrandDomains(brandId: number | string): any[] {
    const brand = this.brands.find((b) => b.id === brandId);
    if (brand && Array.isArray((brand as any).domains) && (brand as any).domains.length > 0) {
      return (brand as any).domains;
    }
    if (!this.domains || !Array.isArray(this.domains)) return [];
    return this.domains.filter((d) => d.branding_id === brandId);
  }

  hasBrandDomains(brandId: number | string): boolean {
    return this.getBrandDomains(brandId).length > 0;
  }

  // --- JSON EDIT & UPLOAD ACTIONS (INSIDE BRAND FORM & DIRECT) ---
  applyJsonToForm(parsed: any): void {
    if (!parsed || typeof parsed !== 'object') return;

    let b = parsed.brand || {};
    if (!parsed.brand && (parsed.colors || parsed.typography || parsed.shape || parsed.identity || parsed.meta)) {
      b = parsed;
    }

    const colors = b.colors || {};
    const colorsDark = b.colors?.dark || b.colors_dark || {};
    const typo = b.typography || {};
    const shape = b.shape || {};
    const idn = b.identity || {};
    const meta = b.meta || {};
    const support = b.support || {};
    const social = b.social || {};

    const patch: any = {};
    if (parsed.name) patch.name = parsed.name;
    else if (idn.display_name) patch.name = idn.display_name;

    const resolvedDesc = parsed.description !== undefined
      ? parsed.description
      : (b.meta?.description !== undefined
        ? b.meta.description
        : (b.identity?.description !== undefined
          ? b.identity.description
          : (b.description !== undefined ? b.description : undefined)));

    if (resolvedDesc !== undefined) {
      patch.description = resolvedDesc;
      patch.meta_description = resolvedDesc;
    }

    if (parsed.is_active !== undefined) patch.is_active = parsed.is_active;
    if (parsed.is_default !== undefined) patch.is_default = parsed.is_default;

    // Colors Light
    if (colors.primary) patch.primary_color = colors.primary;
    if (colors.secondary) patch.secondary_color = colors.secondary;
    if (colors.accent) patch.accent_color = colors.accent;
    if (colors.heading) patch.heading_color = colors.heading;
    if (colors.text) patch.text_color = colors.text;
    if (colors.bg) patch.bg_color = colors.bg;
    if (colors.surface || colors.card || colors.sheet) patch.surface_color = colors.surface || colors.card || colors.sheet;
    if (colors.text_muted) patch.text_muted_color = colors.text_muted;
    if (colors.success) patch.success_color = colors.success;
    if (colors.warning) patch.warning_color = colors.warning;
    if (colors.error) patch.error_color = colors.error;
    if (colors.task) patch.task_color = colors.task;
    if (colors.completed) patch.completed_color = colors.completed;
    if (colors.gradient_start) patch.gradient_start = colors.gradient_start;
    if (colors.gradient_end) patch.gradient_end = colors.gradient_end;

    // Colors Dark
    if (colorsDark.primary) patch.dark_primary_color = colorsDark.primary;
    if (colorsDark.secondary) patch.dark_secondary_color = colorsDark.secondary;
    if (colorsDark.accent) patch.dark_accent_color = colorsDark.accent;
    if (colorsDark.heading) patch.dark_heading_color = colorsDark.heading;
    if (colorsDark.text) patch.dark_text_color = colorsDark.text;
    if (colorsDark.bg) patch.dark_bg_color = colorsDark.bg;
    if (colorsDark.surface || colorsDark.card || colorsDark.sheet) patch.dark_surface_color = colorsDark.surface || colorsDark.card || colorsDark.sheet;
    if (colorsDark.text_muted) patch.dark_text_muted_color = colorsDark.text_muted;
    if (colorsDark.success) patch.dark_success_color = colorsDark.success;
    if (colorsDark.warning) patch.dark_warning_color = colorsDark.warning;
    if (colorsDark.error) patch.dark_error_color = colorsDark.error;
    if (colorsDark.task) patch.dark_task_color = colorsDark.task;
    if (colorsDark.completed) patch.dark_completed_color = colorsDark.completed;
    if (colorsDark.gradient_start) patch.dark_gradient_start = colorsDark.gradient_start;
    if (colorsDark.gradient_end) patch.dark_gradient_end = colorsDark.gradient_end;

    // Typography
    if (typo.source) patch.font_source = typo.source;
    if (typo.family) patch.font_family = typo.family;
    if (typo.family_ar) patch.font_family_ar = typo.family_ar;
    if (typo.google_family) patch.google_family = typo.google_family;
    if (typo.base_size !== undefined) patch.base_size = Number(typo.base_size);

    // Defaults
    const defaults = b.defaults || {};
    if (defaults.theme) patch.default_theme = defaults.theme;
    if (defaults.language) patch.default_language = defaults.language;

    // Shape
    if (shape.radius !== undefined) patch.radius = Number(shape.radius);
    if (shape.button_radius !== undefined) patch.button_radius = Number(shape.button_radius);
    if (shape.shell_max !== undefined) patch.shell_max = Number(shape.shell_max);

    // Identity & Meta
    if (idn.display_name !== undefined) patch.display_name = idn.display_name;
    if (idn.display_name_ar !== undefined) patch.display_name_ar = idn.display_name_ar;
    if (idn.tagline !== undefined) patch.tagline = idn.tagline;
    if (idn.tagline_ar !== undefined) patch.tagline_ar = idn.tagline_ar;
    if (meta.title !== undefined) patch.title = meta.title;
    if (meta.title_ar !== undefined) patch.title_ar = meta.title_ar;
    if (meta.description !== undefined) patch.meta_description = meta.description;
    if (meta.description_ar !== undefined) patch.meta_description_ar = meta.description_ar;

    // Support & Social
    if (support.email) patch.support_email = support.email;
    if (support.phone) patch.support_phone = support.phone;
    if (support.whatsapp) patch.support_whatsapp = support.whatsapp;
    if (support.website) patch.support_website = support.website;
    if (social.instagram) patch.social_instagram = social.instagram;
    if (social.x) patch.social_x = social.x;
    if (social.youtube) patch.social_youtube = social.youtube;
    if (social.tiktok) patch.social_tiktok = social.tiktok;
    if (social.snapchat) patch.social_snapchat = social.snapchat;
    if (social.linkedin) patch.social_linkedin = social.linkedin;
    if (b.flags?.powered_by !== undefined) patch.powered_by = b.flags.powered_by;

    // Legal
    const legal = b.legal || {};
    if (legal.terms_url) patch.terms_url = legal.terms_url;
    if (legal.privacy_url) patch.privacy_url = legal.privacy_url;

    // Assets - Only overwrite form logo if new non-empty URL is provided in JSON
    const logoUrl = this.extractUrl(parsed.logo) || this.extractUrl(b.assets?.logo);
    const logoDarkUrl = this.extractUrl(parsed.logo_dark) || this.extractUrl(b.assets?.logo_dark);
    const iconUrl = this.extractUrl(parsed.icon) || this.extractUrl(b.assets?.icon);

    if (logoUrl && typeof logoUrl === 'string' && logoUrl.trim().length > 0) patch.logo_url = logoUrl;
    if (logoDarkUrl && typeof logoDarkUrl === 'string' && logoDarkUrl.trim().length > 0) patch.logo_dark_url = logoDarkUrl;
    if (iconUrl && typeof iconUrl === 'string' && iconUrl.trim().length > 0) patch.icon_url = iconUrl;

    this.brandForm.patchValue(patch);
    this.brandForm.markAsDirty();
    this.cdRef.detectChanges();
  }

  openEditJsonFromForm(): void {
    const val = this.brandForm.value;
    const fullJson = this.buildFullBrandJson(val, this.editingBrand);

    this.jsonModalBrand = this.editingBrand || ({ id: 0, name: val.name || 'New Brand' } as any);
    this.rawJsonString = JSON.stringify(fullJson, null, 2);
    this.jsonError = '';
    this.isCopied = false;
    this.isJsonModalVisible = true;
  }

  openUploadJsonFromForm(): void {
    this.uploadJsonBrand = this.editingBrand || ({ id: 0, name: this.brandForm.value.name || 'New Brand' } as any);
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
    this.isUploadJsonModalVisible = true;
  }

  getExampleBrandJson(): any {
    return {
      name: 'DigiContests Brand',
      description: 'Premium gamified engagement and interactive campaign portal.',
      is_active: true,
      is_default: false,
      brand: {
        identity: {
          display_name: 'DigiContests',
          display_name_ar: 'ديجي كونتيستس',
          tagline: 'Scan, Play, Win',
          tagline_ar: 'امسح، العب، واربح',
          description: 'Join DigiContests campaigns straight from your browser. Scan a QR code, play and win.',
          description_ar: 'انضم إلى حملات ديجي كونتيستس مباشرة من متصفحك. امسح رمز الاستجابة السريعة، العب واربح.'
        },
        meta: {
          title: 'DigiContests | Win Amazing Prizes',
          title_ar: 'ديجي كونتيستس | اربح جوائز مذهلة',
          description: 'Engage and participate in exclusive brand campaigns, scan QR codes and win rewards instantly.',
          description_ar: 'شارك في حملات تفاعلية حصرية، امسح رمز الاستجابة السريعة واربح جوائز فورية.'
        },
        colors: {
          primary: '#FCBA03',
          secondary: '#122331',
          accent: '#FCBA03',
          bg: '#F7F6F9',
          card: '#FFFFFF',
          surface: '#FFFFFF',
          surface_alt: '#EBEBEB',
          sheet: '#FFFFFF',
          heading: '#122331',
          text: '#122331',
          text_muted: '#6B7280',
          on_primary: '#1D1D1B',
          card_border: '#EBEBEB',
          card_alt: '#FAFAFA',
          gradient_start: '#FFEAB0',
          gradient_end: '#FFFFFF',
          task: '#FFF6D9',
          success: '#1F9254',
          warning: '#D97706',
          error: '#EF233C',
          completed: '#1F9254',
          disabled: '#F1F1F1',
          grey_1: '#E5E7EB',
          grey_2: '#D1D5DB',
          grey_3: '#9CA3AF',
          grey_4: '#6B7280',
          grey_5: '#3B475C',
          grey_6: '#122331'
        },
        colors_dark: {
          primary: '#FCBA03',
          secondary: '#FFD451',
          accent: '#FCBA03',
          bg: '#0D1620',
          card: '#152331',
          surface: '#152331',
          surface_alt: '#1E3040',
          sheet: '#152331',
          heading: '#FFFFFF',
          text: '#FFFFFF',
          text_muted: '#9CA3AF',
          on_primary: '#1D1D1B',
          card_border: '#24384A',
          card_alt: '#101C27',
          gradient_start: '#4A3505',
          gradient_end: '#0D1620',
          task: '#3A2B04',
          success: '#34D399',
          warning: '#F59E0B',
          error: '#FF5A6E',
          completed: '#34D399',
          disabled: '#22303F',
          grey_1: '#22303F',
          grey_2: '#3C4B5C',
          grey_3: '#6B7A8C',
          grey_4: '#9CA3AF',
          grey_5: '#C0C4CB',
          grey_6: '#E5E7EB'
        },
        typography: {
          source: 'google',
          family: 'Inter',
          family_ar: 'Vazirmatn',
          heading_family: 'Inter',
          google_family: 'Inter:wght@300;400;500;600;700;800',
          google_family_ar: 'Vazirmatn:wght@300;400;500;600;700;800',
          fallback: 'sans-serif',
          base_size: 16
        },
        shape: {
          radius: 12,
          radius_lg: 24,
          button_radius: 12,
          shell_max: 400
        },
        assets: {
          logo: {
            access: 'public',
            path: '/vault/example-logo-light.png',
            name: 'logo-light.png',
            type: 'image'
          },
          logo_dark: {
            access: 'public',
            path: '/vault/example-logo-dark.png',
            name: 'logo-dark.png',
            type: 'image'
          },
          icon: {
            access: 'public',
            path: '/vault/example-favicon.png',
            name: 'favicon.png',
            type: 'image'
          },
          og_image: null
        },
        support: {
          email: 'support@digicontests.com',
          phone: '+966500000000',
          whatsapp: '+966500000000',
          website: 'https://digicontests.com'
        },
        social: {
          x: 'https://x.com/digicontests',
          instagram: 'https://instagram.com/digicontests',
          tiktok: '',
          snapchat: '',
          youtube: '',
          linkedin: ''
        },
        flags: {
          powered_by: true
        },
        legal: {
          terms_url: 'https://digicontests.com/terms',
          privacy_url: 'https://digicontests.com/privacy'
        },
        defaults: {
          theme: 'light',
          language: 'en'
        }
      }
    };
  }

  openExampleJsonModal(): void {
    const example = this.getExampleBrandJson();
    this.rawJsonString = JSON.stringify(example, null, 2);
    this.jsonModalBrand = { id: 0, name: 'Complete Example Schema' } as any;
    this.isExampleJsonModal = true;
    this.jsonError = '';
    this.isCopied = false;
    this.isJsonModalVisible = true;
  }

  openImportJsonModal(): void {
    this.uploadJsonBrand = null;
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
    this.isUploadJsonModalVisible = true;
  }

  openEditJsonModal(brand: BrandItem): void {
    this.isExampleJsonModal = false;
    this.jsonModalBrand = brand;
    this.jsonError = '';
    this.isCopied = false;

    const fullJson = this.buildFullBrandJson(brand, brand);
    this.rawJsonString = JSON.stringify(fullJson, null, 2);
    this.isJsonModalVisible = true;
  }

  closeEditJsonModal(): void {
    this.isJsonModalVisible = false;
    this.isExampleJsonModal = false;
    this.jsonModalBrand = null;
    this.jsonError = '';
    this.rawJsonString = '';
  }

  prettifyJson(): void {
    try {
      const parsed = JSON.parse(this.rawJsonString);
      this.rawJsonString = JSON.stringify(parsed, null, 2);
      this.jsonError = '';
    } catch (e: any) {
      this.jsonError = 'Invalid JSON: ' + (e?.message || 'Syntax error');
    }
  }

  copyJsonToClipboard(): void {
    if (!this.rawJsonString) return;
    navigator.clipboard.writeText(this.rawJsonString).then(() => {
      this.isCopied = true;
      this.sharedService.showSuccess('JSON copied to clipboard');
      setTimeout(() => (this.isCopied = false), 2000);
    });
  }

  downloadJson(): void {
    if (!this.rawJsonString) return;
    const blob = new Blob([this.rawJsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(this.jsonModalBrand?.name || 'brand').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-branding.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  triggerEditJsonFileInput(): void {
    if (this.editJsonFileInput) {
      this.editJsonFileInput.nativeElement.value = '';
      this.editJsonFileInput.nativeElement.click();
    }
  }

  onEditJsonFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const text = e.target.result as string;
          const parsed = JSON.parse(text);
          this.rawJsonString = JSON.stringify(parsed, null, 2);
          this.jsonError = '';
          this.sharedService.showSuccess(`Loaded ${file.name} into editor`);
          this.cdRef.detectChanges();
        } catch (err: any) {
          this.jsonError = 'Invalid JSON file: ' + (err?.message || 'Syntax error');
          this.cdRef.detectChanges();
        }
      };
      reader.readAsText(file);
    }
  }

  saveBrandJson(): void {
    if (!this.rawJsonString) return;
    this.jsonError = '';

    let parsed: any;
    try {
      parsed = JSON.parse(this.rawJsonString);
    } catch (e: any) {
      this.jsonError = 'Invalid JSON syntax: ' + (e?.message || '');
      return;
    }

    if (!parsed || typeof parsed !== 'object') {
      this.jsonError = 'JSON root must be an object';
      return;
    }

    // Apply values to brandForm
    this.applyJsonToForm(parsed);

    // If standalone modal (not opened from Create/Edit form), set editingBrand if target exists
    if (!this.isBrandModalVisible) {
      if (this.jsonModalBrand && this.jsonModalBrand.id) {
        this.editingBrand = this.jsonModalBrand;
      } else {
        this.editingBrand = null;
      }
    }

    // Close the JSON editor modal
    this.isJsonModalVisible = false;

    // Directly save and create/update brand on server
    this.saveBrand();
  }

  openUploadJsonModal(brand: BrandItem): void {
    this.uploadJsonBrand = brand;
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
    this.isUploadJsonModalVisible = true;
  }

  closeUploadJsonModal(): void {
    this.isUploadJsonModalVisible = false;
    this.uploadJsonBrand = null;
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
  }

  triggerJsonFileInput(): void {
    if (this.jsonFileInput) {
      this.jsonFileInput.nativeElement.value = '';
      this.jsonFileInput.nativeElement.click();
    }
  }

  onJsonFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.processUploadedFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDropJsonFile(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      this.processUploadedFile(event.dataTransfer.files[0]);
    }
  }

  processUploadedFile(file: File): void {
    if (!file.name.toLowerCase().endsWith('.json') && file.type !== 'application/json') {
      this.uploadJsonError = 'Please choose a valid .json file';
      return;
    }

    this.uploadedJsonFileName = file.name;
    this.uploadJsonError = '';
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const text = e.target.result as string;
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== 'object') {
          throw new Error('Root JSON must be an object');
        }
        this.uploadedJsonData = parsed;
        this.cdRef.detectChanges();
      } catch (err: any) {
        this.uploadedJsonData = null;
        this.uploadJsonError = 'Invalid JSON: ' + (err?.message || 'Syntax error');
        this.cdRef.detectChanges();
      }
    };
    reader.readAsText(file);
  }

  clearUploadedJsonFile(): void {
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
  }

  saveUploadedJson(): void {
    if (!this.uploadedJsonData) return;
    const parsed = this.uploadedJsonData;

    // Apply values to brandForm
    this.applyJsonToForm(parsed);

    if (!this.isBrandModalVisible) {
      if (this.uploadJsonBrand && this.uploadJsonBrand.id) {
        this.editingBrand = this.uploadJsonBrand;
      } else {
        this.editingBrand = null;
      }
    }

    // Close upload modal
    this.isUploadJsonModalVisible = false;

    // Directly save and create/update brand on server
    this.saveBrand();
  }
}
